// Display text names the live-streaming unit "ULLFU" (Ultra Low Latency Forwarding Unit). Visible
// text only: code names (ULLFU*, sfu*), API paths and console logs still say ULLFU.
/**
 * Per-site Settings — the selected node's own settings screen, reached from the cloud.
 *
 * ─── WHY IT IS A SITE SCREEN, NOT A PLATFORM ONE ───
 *
 * Nothing on this page belongs to the cloud. Retention, disk caps, recording defaults and the
 * licence are properties of ONE machine, answered by that machine over the tunnel — which is why
 * the menu entry that opens it carries the site's name, and why the page is unreachable in the
 * All Sites scope: there is no such thing as the estate's retention.
 *
 * It follows that an offline site has no settings to show. The page says the site is unreachable
 * rather than rendering empty fields, because an empty retention box is indistinguishable from
 * "unlimited" and an operator would set it on the strength of that.
 *
 * ─── WHAT IS DELIBERATELY NOT HERE ───
 *
 *   Cloud connection — editing it from the cloud means editing the tunnel carrying the edit, and
 *     a wrong value disconnects the site silently, with no way back but the machine itself.
 *   Licence upload  — a .lic is bound to a machine fingerprint that only exists on the node.
 *   Backup / DR and "clear all recordings" — destructive or long-running work that needs the
 *     operator in front of the box.
 *
 * All four stay on the node's own UI, and the page says so instead of leaving a gap.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Cog6ToothIcon,
  PuzzlePieceIcon,
  ChevronRightIcon,
  ArrowTopRightOnSquareIcon,
  CircleStackIcon,
  KeyIcon,
  ServerIcon,
  UserCircleIcon,
  VideoCameraIcon,
  FilmIcon,
  SignalIcon,
  ArrowPathIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { StatusTag, Callout, StatusPill, initialsOf, CameraTagsManager, PtzDetectionCard, PageTitle } from '@trinetra/shared';
import { cloudCameraTagsApi } from '../api/cameraTags.adapter';
import { useAuth } from '../contexts/AuthContext';
import { useNode } from '../contexts/NodeContext';
import {
  getNodeStorage,
  getNodeStorageConfig,
  setNodeStorageConfig,
  getNodeRecordingConfig,
  getNodeLicense,
} from '../services/api.service';

function formatBytes(bytes?: number) {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(i > 1 ? 2 : 0)} ${units[i]}`;
}

/** Fill colour comes from the theme vars: Tailwind v3 drops `/opacity` on a bare `var()`. */
function StorageBar({ used, max }: { used: number; max: number }) {
  const pct = max > 0 ? Math.min((used / max) * 100, 100) : 0;
  const fill = pct > 90 ? 'var(--destructive)' : pct > 70 ? 'var(--warning)' : 'var(--brand)';
  return (
    <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{ width: `${pct}%`, backgroundColor: fill }}
      />
    </div>
  );
}

function MetricTile({ label, value, icon: Icon }: any) {
  return (
    <div className="rounded-xl border border-border bg-background p-3.5">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        <p className="text-[10px] font-medium uppercase tracking-wider">{label}</p>
      </div>
      <p className="mt-1.5 text-[18px] font-semibold tabular-nums text-foreground">{value}</p>
    </div>
  );
}

function Panel({ title, description, icon: Icon, actions = null, children }: any) {
  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4">
        <div className="flex min-w-0 items-start gap-2.5">
          {Icon && (
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-light">
              <Icon className="h-4 w-4 text-brand" />
            </span>
          )}
          <div className="min-w-0">
            <h3 className="text-[14px] font-semibold text-foreground">{title}</h3>
            {description && <p className="mt-0.5 text-[12px] text-muted-foreground">{description}</p>}
          </div>
        </div>
        {actions}
      </header>
      <div className="space-y-5 p-5">{children}</div>
    </section>
  );
}

export default function SettingsPage() {
  const { user, hasPermission } = useAuth();
  const { selectedNode, selectedNodeId } = useNode();
  const canEdit = hasPermission('settings.edit');

  // Starts on ?section= when a link names one (the camera list's "Manage camera tags").
  const [searchParams] = useSearchParams();
  const [requestedSection, setSection] = useState(() => searchParams.get('section') || 'general');

  const [storage, setStorage] = useState<any>(null);
  const [storageError, setStorageError] = useState('');
  const [loadingStorage, setLoadingStorage] = useState(false);

  const [limits, setLimits] = useState<any>({ retention_days: '', max_storage_gb: '', env_defaults: {} });
  const [savingLimits, setSavingLimits] = useState(false);
  const [limitsMsg, setLimitsMsg] = useState('');
  const [limitsErr, setLimitsErr] = useState('');

  // Recording pipeline options — the effective value (site override, else its .env default).
  const [pipelineOpts, setPipelineOpts] = useState({
    realtime_playback_enabled: true,
    separate_recording_pipeline: true,
  });
  const [savingPipeline, setSavingPipeline] = useState(false);
  const [pipelineMsg, setPipelineMsg] = useState('');
  const [pipelineErr, setPipelineErr] = useState('');

  const [recording, setRecording] = useState<any>(null);
  const [license, setLicense] = useState<any>(null);

  const nodeId = selectedNodeId;
  const online = selectedNode?.status === 'online';

  async function loadStorage() {
    if (!nodeId) return;
    setLoadingStorage(true);
    setStorageError('');
    try {
      setStorage(await getNodeStorage(nodeId));
    } catch (err: any) {
      setStorage(null);
      setStorageError(
        err.response?.data?.message || 'This site did not answer. Its settings live on the node, so there is nothing to show while it is unreachable.',
      );
    } finally {
      setLoadingStorage(false);
    }
  }

  useEffect(() => {
    if (!nodeId) return;
    let live = true;
    loadStorage();
    getNodeStorageConfig(nodeId)
      .then((d) => {
        if (!live || !d) return;
        setLimits({
          retention_days: d.retention_days ?? '',
          max_storage_gb: d.max_storage_gb ?? '',
          env_defaults: d.env_defaults || {},
        });
        setPipelineOpts({
          realtime_playback_enabled:
            d.realtime_playback_enabled ?? d.env_defaults?.realtime_playback_enabled ?? true,
          separate_recording_pipeline:
            d.separate_recording_pipeline ?? d.env_defaults?.separate_recording_pipeline ?? true,
        });
      })
      .catch(() => {});
    getNodeRecordingConfig(nodeId).then((d) => { if (live) setRecording(d); }).catch(() => {});
    getNodeLicense(nodeId).then((d) => { if (live) setLicense(d); }).catch(() => {});
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  async function saveLimits() {
    if (!nodeId) return;
    setSavingLimits(true);
    setLimitsMsg('');
    setLimitsErr('');
    try {
      await setNodeStorageConfig(nodeId, {
        retention_days: limits.retention_days === '' ? null : Number(limits.retention_days),
        max_storage_gb: limits.max_storage_gb === '' ? null : Number(limits.max_storage_gb),
      });
      setLimitsMsg('Saved on the site — its recording service applies the new limits within a few seconds.');
      loadStorage();
    } catch (err: any) {
      setLimitsErr(err.response?.data?.message || 'The site did not accept the change.');
    } finally {
      setSavingLimits(false);
    }
  }

  async function savePipelineOpts() {
    if (!nodeId) return;
    setSavingPipeline(true);
    setPipelineMsg('');
    setPipelineErr('');
    try {
      await setNodeStorageConfig(nodeId, pipelineOpts);
      setPipelineMsg('Saved on the site — every camera\'s recording switches over within a few seconds.');
      const d = await getNodeStorageConfig(nodeId);
      if (d) {
        setPipelineOpts({
          realtime_playback_enabled:
            d.realtime_playback_enabled ?? d.env_defaults?.realtime_playback_enabled ?? true,
          separate_recording_pipeline:
            d.separate_recording_pipeline ?? d.env_defaults?.separate_recording_pipeline ?? true,
        });
      }
    } catch (err: any) {
      setPipelineErr(err.response?.data?.message || 'The site did not accept the change.');
    } finally {
      setSavingPipeline(false);
    }
  }

  const modeCounts = useMemo(() => {
    const configs = recording?.configs || [];
    return configs.reduce((acc: Record<string, number>, c: any) => {
      const key = c.enabled === false ? 'disabled' : c.mode;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [recording]);

  const maxBytes = storage?.max_storage_gb ? storage.max_storage_gb * 1024 * 1024 * 1024 : 0;
  const usedBytes = storage?.disk?.recordings_bytes || 0;
  const usedPct = maxBytes > 0 ? ((usedBytes / maxBytes) * 100).toFixed(1) : null;

  // ── No site selected: this screen has no meaning for the estate ──
  if (!nodeId) {
    return (
      <div className="space-y-3">
        <PageTitle title="Settings" />
        <Callout tone="info">
          Settings belong to a single site — its disks, its retention, its licence. Pick a site in
          the switcher above and its settings appear here.
        </Callout>
      </div>
    );
  }

  const sections = [
    { key: 'general', label: 'General', hint: 'Your account and this site', icon: UserCircleIcon, show: true },
    { key: 'storage', label: 'Storage', hint: 'Retention, limits, usage', icon: CircleStackIcon, show: hasPermission('settings.view') },
    { key: 'license', label: 'License', hint: 'This site’s entitlement', icon: KeyIcon, show: hasPermission('settings.view') },
    // The site's camera tags — camera metadata, so it follows cameras.edit (checked again on the node).
    { key: 'camera-tags', label: 'Camera tags', hint: 'Departments, placement, PTZ', icon: TagIcon, show: hasPermission('cameras.edit') },
  ].filter((x) => x.show);
  // A section this user can't see (or a stale link) falls back to the first one they can.
  const section = sections.some((x) => x.key === requestedSection) ? requestedSection : sections[0].key;

  return (
    <div className="space-y-3">
      <PageTitle
        title={`${selectedNode?.name || 'Site'} settings`}
        subtitle="Read from this site over the tunnel — the node’s own settings, not the platform’s."
      >
        <StatusPill tone={online ? 'success' : 'danger'} pulse={online}>
          {online ? 'Site online' : 'Site offline'}
        </StatusPill>
        {usedPct && (
          <StatusTag tone={Number(usedPct) > 90 ? 'danger' : Number(usedPct) > 70 ? 'warning' : 'neutral'}>
            {usedPct}% of storage used
          </StatusTag>
        )}
      </PageTitle>

      {!online && (
        <Callout tone="warning">
          This site is offline. Its settings live on the node itself, so nothing can be read or
          changed until the tunnel is back.
        </Callout>
      )}

      <div className="flex flex-col gap-3 lg:flex-row">
        <nav data-tour="settings-sections" className="lg:w-56 lg:shrink-0">
          <div className="flex gap-2 overflow-x-auto pb-1 lg:sticky lg:top-0 lg:flex-col lg:gap-1 lg:overflow-visible lg:pb-0">
            {sections.map((sec) => {
              const Icon = sec.icon;
              const active = section === sec.key;
              return (
                <button
                  key={sec.key}
                  onClick={() => setSection(sec.key)}
                  className={`flex shrink-0 items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors ${
                    active ? 'border-brand bg-brand-light' : 'border-transparent hover:bg-muted'
                  }`}
                >
                  <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-brand' : 'text-muted-foreground'}`} />
                  <span className="min-w-0">
                    <span className={`block whitespace-nowrap text-[13px] text-foreground ${active ? 'font-semibold' : 'font-medium'}`}>
                      {sec.label}
                    </span>
                    <span className="hidden truncate text-[11px] text-muted-foreground lg:block">{sec.hint}</span>
                  </span>
                </button>
              );
            })}

            {/* Integrations is a page of its own, so the rail links out rather than switching. */}
            <Link
              data-tour="settings-integrations"
              to="/settings/integrations"
              className="flex shrink-0 items-center gap-2.5 rounded-xl border border-transparent px-3 py-2.5 text-left transition-colors hover:bg-muted lg:mt-2 lg:rounded-none lg:border-x-0 lg:border-b-0 lg:border-t-border lg:pt-3"
            >
              <PuzzlePieceIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1">
                <span className="block whitespace-nowrap text-[13px] font-medium text-foreground">Integrations</span>
                <span className="hidden truncate text-[11px] text-muted-foreground lg:block">VMS and analytics</span>
              </span>
              <ArrowTopRightOnSquareIcon className="hidden h-3.5 w-3.5 shrink-0 text-muted-foreground lg:block" />
            </Link>
          </div>
        </nav>

        <div className="min-w-0 flex-1 space-y-3">
          {section === 'general' && (
            <>
              <Panel title="Profile" description="The cloud account this browser is signed in as." icon={UserCircleIcon}>
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand text-[15px] font-semibold text-white">
                    {initialsOf(user?.name || user?.email)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-semibold text-foreground">{user?.name || '—'}</p>
                    <p className="truncate text-[12px] text-muted-foreground">{user?.email}</p>
                  </div>
                  {user?.role && <StatusTag tone="brand"><span className="capitalize">{user.role}</span></StatusTag>}
                </div>
              </Panel>

              <Panel
                title="Site"
                description="What the cloud knows about this node without asking it."
                icon={ServerIcon}
              >
                <div data-tour="settings-site" className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <MetricTile label="Status" value={selectedNode?.status || 'unknown'} icon={SignalIcon} />
                  <MetricTile label="Version" value={selectedNode?.version || '—'} icon={ServerIcon} />
                  <MetricTile label="Tunnel" value={selectedNode?.tunnelActive ? 'Active' : 'Down'} icon={ArrowPathIcon} />
                  <MetricTile label="Viewers" value={selectedNode?.activeSessions ?? 0} icon={VideoCameraIcon} />
                </div>
                <div className="space-y-2 border-t border-border pt-4 text-[13px]">
                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">Site ID</span>
                    <span className="truncate font-mono text-foreground">{selectedNode?.nodeId}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">Last seen</span>
                    <span className="text-foreground">
                      {selectedNode?.lastSeen ? new Date(selectedNode.lastSeen).toLocaleString() : '—'}
                    </span>
                  </div>
                </div>
                {(selectedNode?.capabilities || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 border-t border-border pt-4">
                    {selectedNode!.capabilities.map((c) => (
                      <StatusTag key={c} tone="info">{c}</StatusTag>
                    ))}
                  </div>
                )}
              </Panel>

              {/* Integrations — a doorway, not a panel, exactly as on the node's own Settings.
                  Named with the site because a VMS connection is made to ONE site's cameras. */}
              <Link
                to="/settings/integrations"
                className="group flex items-center gap-4 rounded-xl border border-border bg-brand-light p-5 shadow-card transition-colors hover:border-brand"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand text-white">
                  <PuzzlePieceIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-[14px] font-semibold text-foreground">Integrations</h3>
                  <p className="mt-0.5 text-[13px] text-muted-foreground">
                    Connect {selectedNode?.name || 'this site'} to an existing VMS, import its
                    cameras, and exchange detections with third-party analytics modules.
                  </p>
                </div>
                <ChevronRightIcon className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </Link>

              <Panel title="Managed on the site itself" description="Settings the cloud deliberately does not reach." icon={Cog6ToothIcon}>
                <ul data-tour="settings-site-only" className="space-y-2.5 text-[12px] leading-relaxed text-muted-foreground">
                  <li>
                    <span className="font-medium text-foreground">Cloud connection</span> — changing
                    it from here would edit the tunnel carrying the change, and a wrong value
                    disconnects the site with no way back but the machine.
                  </li>
                  <li>
                    <span className="font-medium text-foreground">License upload</span> — a licence
                    file is bound to the machine’s own fingerprint, which only exists on the node.
                  </li>
                  <li>
                    <span className="font-medium text-foreground">Backup &amp; disaster recovery</span>,{' '}
                    <span className="font-medium text-foreground">clearing recordings</span> — long or
                    irreversible work that wants the operator in front of the box.
                  </li>
                </ul>
              </Panel>
            </>
          )}

          {section === 'storage' && (
            <Panel
              title="Storage"
              description="Where this site keeps footage, and how much room is left."
              icon={CircleStackIcon}
              actions={
                <button
                  onClick={loadStorage}
                  disabled={loadingStorage}
                  className="rounded-lg border border-border px-3 py-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted disabled:opacity-50"
                >
                  {loadingStorage ? 'Loading…' : 'Refresh'}
                </button>
              }
            >
              {storageError && <Callout tone="danger">{storageError}</Callout>}

              <div className="space-y-3 rounded-xl border border-border bg-background p-4">
                <div>
                  <p className="text-[13px] font-medium text-foreground">Storage limits</p>
                  <p className="text-[11px] text-muted-foreground">
                    Recording retention and disk cap for this site. Empty = the node’s own default.
                    0 = unlimited.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:items-end">
                  <div>
                    <label className="mb-1 block text-[12px] font-medium text-muted-foreground">Retention (days)</label>
                    <input
                      type="number"
                      min="0"
                      value={limits.retention_days}
                      disabled={!canEdit || !online}
                      placeholder={limits.env_defaults?.retention_days != null ? `${limits.env_defaults.retention_days} (default)` : 'site default'}
                      onChange={(e) => setLimits((p: any) => ({ ...p, retention_days: e.target.value }))}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-[13px] text-foreground outline-none placeholder:text-muted-foreground focus:border-brand focus:ring-1 focus:ring-brand disabled:opacity-50"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[12px] font-medium text-muted-foreground">Max storage (GB)</label>
                    <input
                      type="number"
                      min="0"
                      value={limits.max_storage_gb}
                      disabled={!canEdit || !online}
                      placeholder={limits.env_defaults?.max_storage_gb != null ? `${limits.env_defaults.max_storage_gb} (default)` : 'site default'}
                      onChange={(e) => setLimits((p: any) => ({ ...p, max_storage_gb: e.target.value }))}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-[13px] text-foreground outline-none placeholder:text-muted-foreground focus:border-brand focus:ring-1 focus:ring-brand disabled:opacity-50"
                    />
                  </div>
                  {canEdit && (
                    <button
                      onClick={saveLimits}
                      disabled={savingLimits || !online}
                      className="rounded-lg bg-brand px-4 py-2 text-[13px] font-medium text-white transition-colors hover:bg-brand-dark disabled:opacity-50"
                    >
                      {savingLimits ? 'Saving…' : 'Save limits'}
                    </button>
                  )}
                </div>
                {limitsMsg && <Callout tone="success">{limitsMsg}</Callout>}
                {limitsErr && <Callout tone="danger">{limitsErr}</Callout>}
                {!canEdit && (
                  <p className="text-[11px] text-muted-foreground">
                    Your role can read these but not change them.
                  </p>
                )}
              </div>

              <div className="space-y-3 rounded-xl border border-border bg-background p-4">
                <div>
                  <p className="text-[13px] font-medium text-foreground">Recording pipeline</p>
                  <p className="text-[11px] text-muted-foreground">
                    Applies to all cameras on this site within a few seconds of saving.
                  </p>
                </div>
                {([
                  {
                    key: 'realtime_playback_enabled' as const,
                    label: 'Realtime recording playback',
                    on: 'Records 4-second fragments that are playable within seconds, then joins them into 5-minute segments.',
                    off: 'Writes 5-minute segments directly — less CPU and disk activity, but recent footage is playable only after its 5-minute segment closes.',
                  },
                  {
                    key: 'separate_recording_pipeline' as const,
                    label: 'Separate H265 pipeline (record from camera main stream)',
                    on: 'Each camera is recorded from its own main RTSP stream at native codec and resolution (H265 stays H265).',
                    off: 'The camera is pulled once (by live view); recording still runs its own pipeline, but it reads the live stream from the ULLFU instead of the camera. H265 cameras are recorded as the live H264 transcode (lower resolution), and cameras with a sub-stream are recorded from the sub-stream.',
                  },
                ]).map((opt) => (
                  <div key={opt.key} className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-[13px] text-foreground">{opt.label}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {pipelineOpts[opt.key] ? opt.on : opt.off}
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={pipelineOpts[opt.key]}
                      aria-label={opt.label}
                      disabled={!canEdit || !online}
                      onClick={() => setPipelineOpts((p) => ({ ...p, [opt.key]: !p[opt.key] }))}
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${pipelineOpts[opt.key] ? 'bg-brand' : 'bg-border'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${pipelineOpts[opt.key] ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                ))}
                {canEdit && (
                  <button
                    onClick={savePipelineOpts}
                    disabled={savingPipeline || !online}
                    className="rounded-lg bg-brand px-4 py-2 text-[13px] font-medium text-white transition-colors hover:bg-brand-dark disabled:opacity-50"
                  >
                    {savingPipeline ? 'Saving…' : 'Save pipeline options'}
                  </button>
                )}
                {pipelineMsg && <Callout tone="success">{pipelineMsg}</Callout>}
                {pipelineErr && <Callout tone="danger">{pipelineErr}</Callout>}
              </div>

              {recording?.defaults && (
                <div className="space-y-3 rounded-xl border border-border bg-background p-4">
                  <div>
                    <p className="text-[13px] font-medium text-foreground">Recording engine</p>
                    <p className="text-[11px] text-muted-foreground">
                      Node-level values from the site’s own configuration. Recording mode is set per
                      camera.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <MetricTile label="Retained block" value={`${Math.round((recording.defaults.segment_seconds || 300) / 60)} min`} />
                    <MetricTile label="Pre-roll buffer" value={`${Math.round((recording.defaults.fragment_ttl_seconds || 900) / 60)} min`} />
                    <MetricTile label="Max pre-roll" value={`${recording.defaults.max_pre_roll_seconds ?? '—'} s`} />
                    <MetricTile
                      label="Clip retention"
                      value={`${recording.defaults.retention_days_triggered ?? recording.defaults.retention_days ?? '—'} days`}
                    />
                  </div>
                  {Object.keys(modeCounts).length > 0 && (
                    <div className="flex flex-wrap gap-2 border-t border-border pt-3">
                      {Object.entries(modeCounts).map(([mode, count]) => (
                        <StatusTag key={mode} tone={mode === 'disabled' ? 'neutral' : mode === 'continuous' ? 'success' : 'info'}>
                          {count as number} {mode.replace('_', ' + ')}
                        </StatusTag>
                      ))}
                    </div>
                  )}
                  {recording.defaults.retention_enforce === 'log' && (
                    <Callout tone="warning">
                      Dry-run mode: this site reports what it would delete, and deletes nothing.
                    </Callout>
                  )}
                </div>
              )}

              {storage && (
                <>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-[12px]">
                      <span className="text-muted-foreground">Recording storage</span>
                      <span className="font-medium text-foreground">
                        {formatBytes(usedBytes)}
                        {maxBytes > 0 && <span className="text-muted-foreground"> / {formatBytes(maxBytes)}</span>}
                        {usedPct && <span className="ml-1 text-muted-foreground">({usedPct}%)</span>}
                      </span>
                    </div>
                    {maxBytes > 0
                      ? <StorageBar used={usedBytes} max={maxBytes} />
                      : <p className="text-[11px] italic text-muted-foreground">No disk cap configured on this site.</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <MetricTile label="Recordings" icon={FilmIcon} value={formatBytes(storage.disk?.recordings_bytes)} />
                    <MetricTile label="Live cache" icon={SignalIcon} value={formatBytes(storage.disk?.live_bytes)} />
                    <MetricTile label="Segments" icon={CircleStackIcon} value={storage.database?.total_segments?.toLocaleString() || 0} />
                    <MetricTile label="Cameras" icon={VideoCameraIcon} value={storage.database?.camera_count || 0} />
                  </div>

                  {storage.cameras?.length > 0 && (
                    <div className="overflow-x-auto rounded-lg border border-border">
                      <table className="w-full text-left text-[12px]">
                        <thead className="bg-muted">
                          <tr>
                            <th className="px-3 py-2 font-medium text-muted-foreground">Camera</th>
                            <th className="px-3 py-2 text-right font-medium text-muted-foreground">Segments</th>
                            <th className="px-3 py-2 text-right font-medium text-muted-foreground">Size</th>
                            <th className="hidden px-3 py-2 text-right font-medium text-muted-foreground md:table-cell">Oldest</th>
                            <th className="hidden px-3 py-2 text-right font-medium text-muted-foreground md:table-cell">Newest</th>
                          </tr>
                        </thead>
                        <tbody>
                          {storage.cameras.map((cam: any) => (
                            <tr key={cam.camera_id} className="border-t border-border">
                              <td className="max-w-[220px] truncate px-3 py-2 font-medium text-foreground">{cam.camera_name}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{cam.segment_count?.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right tabular-nums font-medium text-foreground">{formatBytes(cam.total_size)}</td>
                              <td className="hidden px-3 py-2 text-right text-muted-foreground md:table-cell">
                                {cam.oldest_segment ? new Date(cam.oldest_segment).toLocaleDateString() : '—'}
                              </td>
                              <td className="hidden px-3 py-2 text-right text-muted-foreground md:table-cell">
                                {cam.newest_segment ? new Date(cam.newest_segment).toLocaleDateString() : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </Panel>
          )}

          {section === 'camera-tags' && hasPermission('cameras.edit') && (
            <>
              <Panel
                title="Camera tags"
                description="Labels this site's camera list can filter on — departments, indoor/outdoor, zones. Unlike groups, tags don't change who can see a camera or which AI models run on it."
                icon={TagIcon}
              >
                <CameraTagsManager api={cloudCameraTagsApi} />
              </Panel>
              <Panel
                title="PTZ detection"
                description="Which of this site's cameras can pan, tilt or zoom, as reported by the camera over ONVIF. Drives the PTZ filter."
                icon={VideoCameraIcon}
              >
                <PtzDetectionCard api={cloudCameraTagsApi} />
              </Panel>
            </>
          )}

          {section === 'license' && (
            <Panel
              title="License"
              description="The entitlement signed for this site’s hardware."
              icon={KeyIcon}
            >
              {!license && <p className="text-[12px] text-muted-foreground">Asking the site…</p>}
              {license && (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusPill tone={license.isValid ? (license.degraded ? 'warning' : 'success') : 'danger'}>
                      {license.status}
                    </StatusPill>
                    {license.mode && <StatusTag tone="neutral">mode: {license.mode}</StatusTag>}
                    {license.daysRemaining != null && (
                      <StatusTag tone={license.daysRemaining < 30 ? 'warning' : 'neutral'}>
                        {license.daysRemaining} days remaining
                      </StatusTag>
                    )}
                    {license.graceDaysRemaining != null && (
                      <StatusTag tone="warning">{license.graceDaysRemaining} grace days</StatusTag>
                    )}
                  </div>

                  {license.message && <Callout tone={license.isValid ? 'info' : 'danger'}>{license.message}</Callout>}
                  {license.hint && <p className="text-[12px] text-muted-foreground">{license.hint}</p>}

                  <p className="border-t border-border pt-4 text-[12px] text-muted-foreground">
                    Renewing or raising a limit means uploading a new licence file, which is bound to
                    this machine’s fingerprint — do that on the site’s own Settings page.
                  </p>
                </>
              )}
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}
